import React, { useEffect, useState, Fragment } from 'react';
import Cookies from 'js-cookie';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'lib/auth';
import { getDynamicGreeting } from 'utils/greetings';
import { useSystemCurrencies } from 'components/Layout/api/getCurrencies';
import { Logo } from 'components/Layout';
import { useTheme } from 'hooks/useTheme';

const getFlagUrl = (code) =>
  `https://flagcdn.com/h40/${code?.toLowerCase()}.png`;

export const ChooseCountry = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const greeting = getDynamicGreeting(user?.firstName || '');
  const { data, isLoading } = useSystemCurrencies({ pageNumber: 1 });

  const [selectedCountry, setSelectedCountry] = useState(null);
  const { theme, background } = useTheme(); // Active theme gradient & bg image

  useEffect(() => {
    if (data?.currencies?.length) {
      const savedCode = Cookies.get('system_country');
      const defaultCountry =
        data.currencies.find((c) => c.currencyCode === savedCode) ||
        data.currencies.find((c) => c.currencyCode === 'UGX');
      setSelectedCountry(defaultCountry);
    }
  }, [data]);

  const handleSelect = (country) => {
    setSelectedCountry(country);
    Cookies.set('system_country', country.currencyCode, { expires: 365 });
  };

  const handleContinue = () => {
    navigate('/app');
  };

  return (
    <div
      className={`relative min-h-screen w-full flex items-center justify-center px-4 transition-colors duration-500 ${theme} bg-fixed`}
    >
      {/* Background Pattern */}
      {background && (
        <div className="absolute inset-0 pointer-events-none opacity-10 z-0">
          <img
            src={background}
            alt="Decorative Background"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content Box */}
      <div className="bg-white/20 flex flex-col rounded-3xl backdrop-blur-md shadow-2xl items-center max-w-full p-8 relative z-10 text-center space-y-4">
        <Logo />
        <div className='mb-4'>
          <h1 className="text-xl mb-3 md:text-2xl font-semibold text-white">
            {greeting}
          </h1>
          <p className="text-md text-white">
            Choose a default country for your system.
          </p>
        </div>

        {/* Country Dropdown */}
        {isLoading || !selectedCountry ? (
          <div className="w-64 h-10 rounded-xl bg-white/10 animate-pulse backdrop-blur-md mx-auto" />
        ) : (
          <Menu as="div" className="relative inline-block w-full text-left z-10">
            {({ open }) => (
              <>
                <Menu.Button className="flex items-center justify-center w-full py-6 gap-2 px-4  text-white rounded-xl hover:scale-105 transition">
                  <img
                    src={getFlagUrl(selectedCountry.flagCode)}
                    alt={`${selectedCountry.country} flag`}
                    className="h-5 w-5 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium">
                    {selectedCountry.country}
                  </span>
                  <ChevronDown size={16} />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  show={open}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className={`absolute w-full left-1/2 transform -translate-x-1/2 mt-2 w-72 rounded-2xl ${theme} p-4 shadow-xl backdrop-blur-xl ring-1 ring-white/20 border border-white/10 text-white max-h-96 overflow-y-auto`}>
                    <p className="text-sm font-semibold mb-2">
                      Select System Country
                    </p>
                    <hr className="border-white/10 mb-2" />
                    {data.currencies.map((currency) => (
                      <Menu.Item key={currency.currencyCode}>
                        {({ active }) => (
                          <button
                            onClick={() => handleSelect(currency)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                              active
                                ? 'bg-white/10 text-white'
                                : 'text-white/80 hover:bg-white/10'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <img
                                src={getFlagUrl(currency.flagCode)}
                                alt={`${currency.country} flag`}
                                className="h-5 w-5 rounded-full object-cover"
                              />
                              <span>{currency.country}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <ChevronRight size={12} />
                              {currency.currencyCode}
                            </div>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>
        )}

        {/* Continue Button */}
        {selectedCountry && (
          <button
            onClick={handleContinue}
            className="mt-2 px-6 py-6 w-full text-white text-sm font-medium bg-white/10 backdrop-blur-md rounded-xl hover:scale-105 transition border border-white/20"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  );
};
